import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import AiWorkspace from './AiWorkspace';
import { AiItem, openChat } from '../../services/aiAssistantService';
import './AiAssistant.css';

/**
 * AiStrip — the assistant's entry point (docs/ai-assistant/IMPLEMENTATION-PLAN.md §6).
 *
 * A thin bar fixed at the bottom of every page, mounted once in RoleLayout so it is the same
 * assistant on every screen of every persona. Click it, or press Ctrl+K / ⌘K, to bring the
 * workspace up; click again (or press the shortcut) to put it away. The conversation lives
 * here, so hiding and showing keeps it exactly as it was.
 *
 * When the assistant has changed data, putting the workspace away reloads the page so the screen
 * behind shows the change; the conversation id is kept for this tab (sessionStorage) and reopened.
 */
const CID_KEY = 'anygrc_ai_cid';
const readCid = (): string | null => { try { return sessionStorage.getItem(CID_KEY); } catch { return null; } };
const writeCid = (v: string | null) => { try { if (v) sessionStorage.setItem(CID_KEY, v); else sessionStorage.removeItem(CID_KEY); } catch { /* storage blocked */ } };
const AiStrip: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AiItem[]>([]);
  const [cid, setCidState] = useState<string | null>(readCid);
  const [pending, setPending] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [changed, setChanged] = useState(false);
  const setCid: React.Dispatch<React.SetStateAction<string | null>> = useCallback((v) => {
    setCidState((prev) => {
      const next = typeof v === 'function' ? (v as (p: string | null) => string | null)(prev) : v;
      writeCid(next);
      return next;
    });
  }, []);

  // After a reload, bring the conversation back the first time the workspace opens.
  useEffect(() => {
    if (!open || !cid || items.length) return;
    openChat(cid).then((c) => { setItems(c.transcript); setPending(c.pending?.summary ?? null); })
      .catch(() => setCid(null));
  }, [open, cid, items.length, setCid]);

  const close = useCallback(() => {
    setOpen(false);
    if (changed) window.location.reload();          // the screen behind fetches its data again
  }, [changed]);
  const toggle = useCallback(() => (open ? close() : setOpen(true)), [open, close]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggle]);

  const mac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  const hint = open
    ? pending ? 'A change is waiting for your confirmation' : 'Your conversation stays as it is'
    : items.length ? 'Continue your conversation' : 'Ask anything — “how many open findings?”, “add a risk category Cyber”';

  return (
    <>
      <div className={`aistrip${open ? ' is-open' : ''}`} role="region" aria-label="AI assistant">
        <button type="button" className="aistrip__field" onClick={toggle} aria-expanded={open}
          aria-label={open ? 'Hide the assistant' : 'Open the assistant'} data-testid="BTN_ai_strip_toggle">
          <span className="aistrip__icon"><Sparkles size={15} /></span>
          <span className="aistrip__label">{open ? 'Hide assistant' : 'Ask AI'}</span>
          <span className="aistrip__hint">{hint}</span>
          {pending && !open && <span className="aistrip__flag">1 to confirm</span>}
          <kbd className="aistrip__kbd">{mac ? '⌘' : 'Ctrl'} K</kbd>
        </button>
      </div>
      {open && (
        <AiWorkspace onClose={close}
          store={{ items, setItems, cid, setCid, pending, setPending, input, setInput, onChanged: () => setChanged(true) }} />
      )}
    </>
  );
};

export default AiStrip;
